import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, ArrowRight, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth";
import { useBillingSubscription } from "@/hooks/use-billing-subscription";
import { trackEvent } from "@/lib/analytics";
import { BFCM_2025_CAMPAIGN, isCampaignActive, formatCampaignCountdown } from "@/config/campaigns";

const BANNER_DISMISSED_KEY = 'bfcm_2025_banner_dismissed';

export function BFCMBanner() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isSubscribed, loading } = useBillingSubscription();
  const [isDismissed, setIsDismissed] = useState(() => {
    return localStorage.getItem(BANNER_DISMISSED_KEY) === 'true';
  });
  const [countdown, setCountdown] = useState(formatCampaignCountdown(BFCM_2025_CAMPAIGN));

  // Check if campaign is active
  const isCampaignLive = isCampaignActive(BFCM_2025_CAMPAIGN);
  
  // Show banner if: campaign is live, not dismissed, user is not subscribed (or not loaded yet)
  const shouldShowBanner = isCampaignLive && !isDismissed && !loading && !isSubscribed;

  // Update countdown every minute
  useEffect(() => {
    if (!shouldShowBanner) return;

    const interval = setInterval(() => {
      setCountdown(formatCampaignCountdown(BFCM_2025_CAMPAIGN));
    }, 60000); // Update every minute

    return () => clearInterval(interval);
  }, [shouldShowBanner]);

  // Track banner view
  useEffect(() => {
    if (shouldShowBanner) {
      trackEvent({
        name: 'bfcm_banner_viewed',
        properties: {
          campaign: BFCM_2025_CAMPAIGN.id,
          location: 'homepage',
          isAuthenticated: !!user,
        },
      });
    }
  }, [shouldShowBanner, user]);

  const handleClick = () => {
    trackEvent({
      name: 'bfcm_banner_clicked',
      properties: {
        campaign: BFCM_2025_CAMPAIGN.id,
        location: 'homepage',
        isAuthenticated: !!user,
        destination: user ? 'onboarding' : 'signup',
      },
    });
    
    // If user is logged in, go directly to onboarding pricing
    // If not logged in, go to signup (they'll see campaign pricing after onboarding)
    if (user) {
      navigate('/pricing-selection');
    } else {
      navigate('/signup?campaign=bfcm-2025');
    }
  };

  const handleDismiss = () => {
    trackEvent({
      name: 'bfcm_banner_dismissed',
      properties: {
        campaign: BFCM_2025_CAMPAIGN.id,
        location: 'homepage',
      },
    });
    setIsDismissed(true);
    localStorage.setItem(BANNER_DISMISSED_KEY, 'true');
  };

  if (!shouldShowBanner) {
    return null;
  }

  return (
    <div className="relative bg-gradient-to-r from-purple-600 via-pink-600 to-orange-600 text-white w-full shadow-lg mt-10">
      <div className="w-full px-4 py-3 sm:py-4">
        <div className="max-w-7xl mx-auto">
          {/* Desktop Layout */}
          <div className="hidden sm:flex items-center justify-between gap-4">
            {/* Left - Deal Message */}
            <div className="flex items-center gap-3">
              <div className="bg-yellow-400 text-black px-2 py-1 rounded font-black text-xs uppercase animate-pulse">
                Limited Time
              </div>
              <div className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 flex-shrink-0 text-yellow-300" />
                <div className="flex flex-col -space-y-0.5">
                  <div className="flex items-center gap-2">
                    <span className="font-black text-base uppercase tracking-wide">
                      BLACK FRIDAY:
                    </span>
                    <span className="bg-red-500 text-white px-2 py-0.5 rounded-md font-black text-sm">
                      50% OFF
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="line-through text-white/60">$99/year</span>
                    <span className="text-yellow-300 font-bold text-lg">→ $49.50/year</span>
                    <span className="text-white/80 text-xs">(Save $49.50!)</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Right - CTA and Timer */}
            <div className="flex items-center gap-3">
              <div className="text-right">
                <div className="text-xs text-white/80 uppercase tracking-wide">Ends in</div>
                <div className="font-bold text-base text-yellow-300">{countdown}</div>
              </div>
              <Button
                onClick={handleClick}
                size="lg"
                className="bg-yellow-400 text-black hover:bg-yellow-300 font-black text-base px-6 py-3 h-auto shadow-xl hover:scale-105 transition-all"
              >
                Claim 50% Off Now
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <button
                onClick={handleDismiss}
                className="p-1.5 hover:bg-white/20 rounded transition-colors"
                aria-label="Dismiss banner"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Mobile Layout */}
          <div className="sm:hidden">
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="flex items-center gap-2 flex-1">
                <div className="bg-yellow-400 text-black px-2 py-0.5 rounded font-black text-xs uppercase">
                  Limited
                </div>
                <div className="flex items-center gap-1.5">
                  <Sparkles className="h-4 w-4 text-yellow-300 flex-shrink-0" />
                  <span className="font-black text-sm uppercase">BLACK FRIDAY</span>
                </div>
              </div>
              <button
                onClick={handleDismiss}
                className="p-1 hover:bg-white/20 rounded transition-colors flex-shrink-0"
                aria-label="Dismiss banner"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            
            <div className="flex items-center justify-between mb-2">
              <div>
                <div className="flex items-center gap-2">
                  <span className="bg-red-500 text-white px-2 py-0.5 rounded font-black text-xs">
                    50% OFF
                  </span>
                  <span className="line-through text-white/60 text-xs">$99</span>
                  <span className="text-yellow-300 font-bold text-lg">$49.50</span>
                </div>
                <div className="text-xs text-white/80 mt-0.5">Save $49.50 on annual plan!</div>
              </div>
              <div className="text-right">
                <div className="text-xs text-white/70 uppercase">Ends in</div>
                <div className="font-bold text-sm text-yellow-300">{countdown}</div>
              </div>
            </div>
            
            <Button
              onClick={handleClick}
              className="bg-yellow-400 text-black hover:bg-yellow-300 font-black text-sm px-4 py-2.5 w-full shadow-lg"
            >
              Claim 50% Off Now
              <ArrowRight className="ml-1.5 h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Animated shine effect */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent animate-[shimmer_3s_infinite]" style={{
          backgroundSize: '200% 100%',
          animation: 'shimmer 3s linear infinite',
        }} />
      </div>

      <style>{`
        @keyframes shimmer {
          0% {
            transform: translateX(-100%);
          }
          100% {
            transform: translateX(100%);
          }
        }
      `}</style>
    </div>
  );
}

